// @flow
/** @jsx h */
import { h, Fragment } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { styled } from 'goober';
import { device } from '../../../../utils/breakPoints';
import WebFont from 'webfontloader';
import { SALES_WIDGET_ID } from '../../../salesApp/constants';
import Header from '../../../salesApp/Header';
import {
  useSalesDispatchContext,
  useSalesContext,
} from '../../../../storeProviders/salesAppStore';
import {
  addRequestInterceptor,
  removeRequestInterceptor,
} from '../../../../api/services/useRequest';
import SalesAppErrorSheet from '../../../salesApp/SalesAppErrorSheet';
import RenderBottomSheet from '../../../salesApp/RenderBottomSheet';
import Match from 'preact-router/match';
import { route } from 'preact-router';
import { tracker as saAppTracker } from '../../../../tracking';
import { useSalesAppTracker } from '../../../../components/salesApp/tracker';

export const replaceInitialSalesAppRoute: (
  url: string,
  callback?: Function,
) => void = (url, callback) => {
  if (callback) callback(url);
  route(url + '?initial=true', true);
  route(url);
};
import NoInternet from '../../../salesApp/NoInternet';

const SaleAppUIContainer = styled('div')(
  (props) => `
  * {
   font-family: 'Inter', sans-serif;
  }
  --primaryCTAColor: ${props.saleAppTheme.primaryCTAColor};
  --secondaryCTAColor: ${props.saleAppTheme.secondaryCTAColor};
   width: 100%;
   height: 100vh;
   background-color: transparent;
   display: flex;
   justify-content: center;
   align-items: center;
   -webkit-tap-highlight-color: transparent;
   -webkit-touch-callout: none;
   -webkit-user-select: none;
   -khtml-user-select: none;
   -moz-user-select: none;
   -ms-user-select: none;
   user-select: none;
  .salesAppInnerContainer{
    margin: auto;
    background-color: #ffffff;
    width: 100%;
    height: 100vh;
    padding: 0px;
    position: relative;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    overflow: hidden;
  }
  .childrenContainer {
    height: 100%;
    overflow-y: scroll;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .childrenContainer::-webkit-scrollbar {
    background: transparent;
    display: none;
    overflow-y: scroll;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .childrenContainer::-webkit-scrollbar {
    background: transparent;
    display: none;
  }
  ${device.tabletS`
    .salesAppInnerContainer{
      max-height: 712px;
      max-width: 414px;
      height: 90vh;
      min-height: 640px;
    }
  `}
  .mdc-text-field--focused:not(.mdc-text-field--disabled) .mdc-floating-label {
    color: #1A73E8 !important;
  }
  
  .mdc-text-field--focused:not(.mdc-text-field--disabled) .mdc-floating-label {
    color: #1A73E8 !important;
  }
  
  .mdc-text-field--textarea.mdc-text-field--focused:not(.mdc-text-field--disabled) .mdc-text-field__input:focus {
    border-color: #1A73E8 !important;
  }
  
  .mdc-text-field--textarea.mdc-text-field--focused:not(.mdc-text-field--disabled) {
    border-color: #1A73E8 !important;
  }
  .mdc-text-field .mdc-line-ripple {
    background-color: #1A73E8 !important;
  }
  .mdc-text-field:not(.mdc-text-field--disabled) {
    background-color: #ffffff !important;
  }
  .mdc-button:disabled {
    background-color: rgba(0, 0, 0, 0.12) !important;
    color: rgba(0, 0, 0, 0.37) !important;
  }
  .mdc-text-field--outlined:not(.mdc-text-field--disabled).mdc-text-field--focused
    .mdc-notched-outline__path {
    stroke: #1A73E8 !important;
  }
  .mdc-checkbox::before,
  .mdc-checkbox::after {
    background-color: #1A73E8 !important;
  }
  .mdc-checkbox__native-control:enabled:checked ~ .mdc-checkbox__background,
  .mdc-checkbox__native-control:enabled:indeterminate ~ .mdc-checkbox__background {
    background-color: #1A73E8 !important;
    border-color: #1A73E8 !important;
  }
  canvas {
    image-rendering: pixelated;
  }
`,
);

const PoweredByLogoContainer = styled('div')`
  width: 100%;
  max-width: 100%;
  background-color: #ffffff;
  padding: 0 10px;
  display: flex;
  justify-content: center;
  margin: 0px;
  align-items: center;
  .poweredbyText {
    font-weight: 500;
    font-size: 12px;
    color: #a8a3a3;
  }
  .instacredLogo {
    margin-left: 10px;
    height: 12.5px;
    margin-top: -4px;
  }
`;

const HeaderContainer = styled('div')`
  position: sticky;
  top: 0;
  z-index: 3;
  width: 100%;
  max-width: 100%;
`;

const SaleAppContainer = (props: any) => {
  const [
    messageDataMerchantAgentID,
    setMessageDataMerchantAgentID,
  ] = useState();
  const postMsgRef = useRef();
  const salesDispatch = useSalesDispatchContext();
  const salesAppTracker = useSalesAppTracker();

  const isStringValidJSON = (stringJSON) => {
    let isValid = false;
    try {
      JSON.parse(stringJSON);
      isValid = true;
    } catch (e) {}
    return isValid;
  };

  useEffect(() => {
    WebFont.load({
      google: {
        families: ['Inter:400,500,600,700'],
      },
    });

    const messageListener = (e) => {
      const parentWindowOrigin = document.referrer;

      let postMsgMerchantOrigin;
      if (parentWindowOrigin) {
        try {
          const originUrlObj = new URL(parentWindowOrigin);
          postMsgMerchantOrigin = originUrlObj.origin;
        } catch (e) {}
      }

      if (
        !postMsgMerchantOrigin &&
        e.data &&
        typeof e.data === 'string' &&
        isStringValidJSON(e.data) &&
        e.isTrusted
      ) {
        const messageData = JSON.parse(e.data);
        postMsgMerchantOrigin = messageData.merchantOrigin;
      }

      const isExpectedMerchantOrigin =
        !!postMsgMerchantOrigin && postMsgMerchantOrigin === e.origin;

      if (
        e.data &&
        typeof e.data === 'string' &&
        isStringValidJSON(e.data) &&
        isExpectedMerchantOrigin &&
        e.isTrusted
      ) {
        const messageData = JSON.parse(e.data);
        const salesState = useSalesContext();
        const { mobile, amount } = salesState || {};

        if (messageData.actionName === 'openSalesApp') {
          postMsgRef.current = {
            merchantKey: messageData.merchantKey,
            postMsgMerchantOrigin: postMsgMerchantOrigin,
            theme: messageData.merchantTheme,
            type: messageData.type,
            icScope: messageData.icScope,
            authToken: messageData.authToken,
            merchantAgentId: messageData.merchantAgentId,
          };
          setMessageDataMerchantAgentID(messageData.merchantAgentId);
          salesDispatch({
            type: 'STORE_STATE',
            payload: {
              mobile: messageData.mobile || mobile,
              amount: messageData.amount || amount,
              salesAppInitError: messageData.error,
            },
          });
          salesDispatch({
            type: 'SHOW_SALES_APP',
            payload: {
              merchantData: {
                type: messageData.type,
                merchantKey: messageData.merchantKey,
                merchantName: messageData.merchantName,
                merchantLogo: messageData.merchantLogo,
                merchantAgentId: messageData.merchantAgentId,
                merchantURL: messageData.merchantURL,
                theme: messageData.merchantTheme,
                postMsgMerchantOrigin: postMsgMerchantOrigin,
                authToken: messageData.authToken,
              },
            },
          });
        } else if (messageData.actionName === 'getAuthToken') {
          postMsgRef.current = {
            ...(postMsgRef.current ? postMsgRef.current : {}),
            authToken: messageData.authToken,
          };
          const salesState = useSalesContext();
          const { merchantData } = salesState || {};
          salesDispatch({
            type: 'STORE_STATE',
            payload: {
              merchantData: {
                ...merchantData,
                authToken: messageData.authToken,
              },
            },
          });
        }
      }
    };

    window.addEventListener('message', messageListener);

    return () => {
      window.removeEventListener('message', messageListener);
    };
  }, []);

  useEffect(() => {
    const requestInterceptor = (config) => {
      config.headers = {
        ...config.headers,
        'ic-merchant-key': postMsgRef.current && postMsgRef.current.merchantKey,
        'ic-origin':
          postMsgRef.current && postMsgRef.current.postMsgMerchantOrigin,
        'ic-merchant-sdk-scope':
          postMsgRef.current && postMsgRef.current.icScope,
        'merchant-agent-id':
          postMsgRef.current && postMsgRef.current.merchantAgentId,
        'ic-merchant-sdk-auth':
          postMsgRef.current && postMsgRef.current.authToken,
      };
      return config;
    };
    addRequestInterceptor(requestInterceptor);

    return () => {
      removeRequestInterceptor(requestInterceptor);
    };
  });

  useEffect(() => {
    const fetchSaMerchantAgentId =
      messageDataMerchantAgentID && messageDataMerchantAgentID;
    salesAppTracker.trackPageView('sa_pv_emi_home_module');
    saAppTracker.pushSaAppUserProfile(fetchSaMerchantAgentId);
  }, [messageDataMerchantAgentID]);

  const pageLoadInIframe = () => {
    try {
      return window.self !== window.top;
    } catch (e) {
      return true;
    }
  };

  const saleAppTheme = {
    ...(postMsgRef.current && {
      primaryCTAColor:
        (postMsgRef.current.theme &&
          postMsgRef.current.theme.primaryCTAColor) ||
        '#007E3F',
      secondaryCTAColor:
        (postMsgRef.current.theme &&
          postMsgRef.current.theme.secondaryCTAColor) ||
        '#5faf5d',
    }),
  };

  const handleInitialPageBackClose = (url, path) => {
    const params = new URL(location.origin + url).searchParams;
    if (params.get('initial') === 'true') {
      route(path);
      salesDispatch({
        type: 'SEND_MESSAGE',
        payload: {
          actionName: 'closeSAApp',
          payload: {},
        },
      });
    }
    return null;
  };

  return (
    pageLoadInIframe() && (
      <Fragment>
        <Match>
          {({ url, path }) => handleInitialPageBackClose(url, path)}
        </Match>
        <SaleAppUIContainer saleAppTheme={saleAppTheme || {}}>
          <div className='salesAppInnerContainer' id={SALES_WIDGET_ID}>
            <RenderBottomSheet />
            <SalesAppErrorSheet />
            <HeaderContainer>
              <Header />
            </HeaderContainer>
            <div className='childrenContainer'>{props.children}</div>
            <PoweredByLogoContainer>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  margin: '16px 0px',
                  alignItems: 'center',
                }}>
                <div className='poweredbyText'>Powered by</div>
                <img
                  src='https://iccdn.in/img/instacred-v1.0.svg'
                  className='instacredLogo'
                />
              </div>
            </PoweredByLogoContainer>
          </div>
        </SaleAppUIContainer>
        <NoInternet />
      </Fragment>
    )
  );
};

export default SaleAppContainer;



// WEBPACK FOOTER //
// ./components/partial/container/salesAppContainer/index.js